import React, { useContext, useRef, useState } from "react";
import { FiImage, FiSend, FiX } from "react-icons/fi";
import { AuthContext } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { Avatar, Button } from "../ui";
import { createPost } from "../../api/posts";

export default function PostComposer({ onPosted }) {
  const { user } = useContext(AuthContext);
  const { notify } = useToast();
  const [content, setContent] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const handleImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImageFile(file);
    setPreview(URL.createObjectURL(file));
  };

  const clearImage = () => {
    setImageFile(null);
    setPreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const submit = async () => {
    if (!content.trim() && !imageFile) return;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("content", content.trim());
      if (imageFile) formData.append("image", imageFile);
      const res = await createPost(formData);
      setContent("");
      clearImage();
      notify("Your post is live.", "success");
      onPosted?.(res.data?.post);
    } catch (error) {
      console.error(error);
      notify("We couldn't publish that post. Please try again.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-card">
      <div className="flex gap-3">
        <Avatar name={user?.name} size="md" />
        <div className="flex-1">
          <textarea
            value={content}
            onChange={(e) => setContent(e.target.value)}
            placeholder="Share an update, a project, or an idea with the community..."
            rows={2}
            className="w-full resize-none border-none bg-transparent text-[15px] text-slate-900 placeholder:text-slate-400 focus:outline-none"
          />
          {preview && (
            <div className="relative mt-2 inline-block">
              <img src={preview} alt="Attachment preview" className="max-h-56 rounded-lg border border-slate-200 object-cover" />
              <button
                onClick={clearImage}
                aria-label="Remove image"
                className="absolute -right-2 -top-2 rounded-full bg-slate-900/80 p-1 text-white hover:bg-slate-900"
              >
                <FiX className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between border-t border-slate-100 pt-3">
        <label className="flex cursor-pointer items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium text-slate-500 hover:bg-slate-50">
          <FiImage className="h-[18px] w-[18px] text-emerald-500" />
          Photo
          <input ref={fileRef} type="file" accept="image/*" onChange={handleImage} className="hidden" />
        </label>
        <Button onClick={submit} loading={submitting} disabled={!content.trim() && !imageFile} size="sm">
          <FiSend className="h-4 w-4" /> Post
        </Button>
      </div>
    </div>
  );
}
